import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  ArrowRight,
  Sparkles,
  Zap,
  Loader2,
  AlertTriangle,
  Target,
  TrendingUp,
  Lightbulb,
  Lock,
  Globe,
  Stethoscope,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Link } from "react-router-dom";
import type { PlanType, OrchestratorResult } from "@/lib/agents/types";
import { ApiClient } from "@/lib/api-client";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Helper function to determine locks for each plan
function locksForPlan(planType: PlanType) {
  const locks = [];

  if (planType === "Free") {
    locks.push(
      { agent: "ExternalInsightAgent", feature: "Google Trends + AI Benchmark", requiredPlan: "Pro" },
      { agent: "Surgeon", feature: "Injection payloads (CMS/API/SQL)", requiredPlan: "Premium" },
    );
  }

  if (planType === "Pro") {
    locks.push(
      { agent: "Surgeon", feature: "Competitor + market + auto blog generation + injection", requiredPlan: "Premium" },
    );
  }

  return locks;
}

export function HeroSection() {
  const [url, setUrl] = useState("");
  const [planType, setPlanType] = useState<PlanType>("Free");
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [runResult, setRunResult] = useState<OrchestratorResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentMessage, setCurrentMessage] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [scannedUrl, setScannedUrl] = useState<string>("");  // URL normalized cu https://
  const [isSubmittingEmail, setIsSubmittingEmail] = useState<boolean>(false);

  const progressiveMessages = [
    "🔍 Analizăm poziționarea site-ului tău în Google...",
    "🤖 Verificăm cum este perceput de AI (ChatGPT / Perplexity)...",
    "📊 Analizăm semnalele de autoritate & încredere...",
    "🧠 Calculăm scorul GEO (Generative Engine Optimization)..."
  ];

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isSubmittingEmail || !runResult) return;

    setIsSubmittingEmail(true);
    
    try {
      // Save email to backend for current scan with strategy data
      const response = await fetch('/api/save-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.trim(),
          url: scannedUrl || url.trim(),  // URL normalizat cu https://
          strategy: runResult.strategist.strategy,
          siteContext: runResult.siteContext  // Trimitem și siteContext pentru site_meta/content/technical
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        // Show success message with email ID if available
        alert(`✅ Raportul complet a fost trimis pe ${email.trim()}! Verifică inbox-ul (și spam-ul) în câteva minute.`);
        setEmail("");
      } else {
        // Show error message from API
        alert(`❌ ${result.error || 'A apărut o eroare. Te rugăm să încerci din nou.'}`);
      }
    } catch (error) {
      console.error('Error saving email:', error);
      alert('❌ A apărut o eroare de conexiune. Te rugăm să încerci din nou.');
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  const handleScan = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed || isRunning) return;

    // Auto-fix URL: force HTTPS for all inputs
    let processedUrl = trimmed;
    
    // Remove any existing protocol first
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      processedUrl = trimmed.replace(/^https?:\/\//, '');
    }
    
    // Always add https:// prefix
    processedUrl = `https://${processedUrl}`;

    setScannedUrl(processedUrl);  // Salvăm URL-ul normalizat pentru save-email
    setIsRunning(true);
    setProgress(10);
    setError(null);
    setRunResult(null);
    setCurrentMessage(progressiveMessages[0]);

    try {
      // Use backend API only (Groq-powered)
      setProgress(25);
      setCurrentMessage(progressiveMessages[1]);
      
      const apiResponse = await ApiClient.scanSite(processedUrl);
      setProgress(50);
      setCurrentMessage(progressiveMessages[2]);
      
      // Transform backend response to match frontend expectations
      if (!apiResponse?.success || !apiResponse?.data) {
        throw new Error('Răspuns invalid de la server. Datele lipsesc.');
      }
      
      const backendData = apiResponse.data;
      
      // Convert backend response to OrchestratorResult format
      const transformedResult: OrchestratorResult = {
        planType,
        siteContext: backendData.siteContext,
        externalInsights: backendData.external_insights,
        strategist: {
          strategy: backendData.strategy
        },
        surgeonPlan: undefined, // Backend doesn't provide this yet
        locks: locksForPlan(planType)
      };
      
      setProgress(75);
      setCurrentMessage(progressiveMessages[3]);
      
      // Fake delay for psychological effect (15 seconds total)
      await new Promise(resolve => setTimeout(resolve, 8000));
      
      setProgress(100);
      setRunResult(transformedResult);
      
    } catch (err) {
      setProgress(0);
      
      // Handle API errors with user-friendly messages
      if (err instanceof Error) {
        // Handle common API errors
        if (err.message.includes('400') || err.message.includes('Invalid URL')) {
          setError('URL invalid. Te rugăm să verifici adresa și să încerci din nou.');
        } else if (err.message.includes('413') || err.message.includes('Content Too Large')) {
          setError('Site-ul este prea mare pentru procesare. Încearcă cu o pagină specifică.');
        } else if (err.message.includes('500') || err.message.includes('Eroare internă')) {
          setError('Server temporar indisponibil. Te rugăm să încerci din nou în câteva minute.');
        } else if (err.message.includes('502') || err.message.includes('AI temporar indisponibil')) {
          setError('Serviciul AI este temporar indisponibil. Te rugăm să încerci din nou.');
        } else if (err.message.includes('CORS')) {
          setError('Eroare de securitate CORS. Încearcă să folosești un proxy sau contactează suportul.');
        } else if (err.message.includes('Răspuns invalid')) {
          setError('Serverul a returnat un răspuns neașteptat. Te rugăm să încerci din nou.');
        } else {
          setError(`Eroare: ${err.message}`);
        }
      } else {
        setError("A apărut o eroare neașteptată. Te rugăm să încerci din nou.");
      }
    } finally {
      setIsRunning(false);
      setCurrentMessage("");
    }
  };

  return (
    <ErrorBoundary>
      <section id="top" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-subtle pt-16">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent border border-primary/20 mb-8"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-accent-foreground">
              Prima soluție SEO + GEO din România
            </span>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
          >
            Site-ul tău nu mai este găsit pe Google?{" "}
            <span className="text-gradient">Pregătește-l pentru era AI.</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10"
          >
            OptiRO.dev este singurul tool care îți aduce clienți prin SEO
            tradițional și recomandări directe în ChatGPT/Perplexity.
          </motion.p>

          {/* Scan Input */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            onSubmit={handleScan}
            className="flex flex-col gap-3 max-w-xl mx-auto mb-12 sm:flex-row"
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                inputMode="url"
                placeholder="Introdu URL-ul (ex: exemplu.ro sau https://exemplu.ro)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-14 pl-12 pr-4 text-base rounded-xl border-2 border-slate-200 focus:border-primary bg-background"
              />
            </div>
            <Button
              type="submit"
              variant="hero"
              size="xl"
              className="group"
              disabled={!url.trim() || isRunning}
            >
              {isRunning ? (
                <>
                  Se rulează agenții
                  <Loader2 className="w-5 h-5 animate-spin" />
                </>
              ) : (
                <>
                  Scanează Gratuit
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </Button>
          </motion.form>

          {/* Removed Plan Selector - focus on scanning only */}

          {/* Scan Result */}
          {(isRunning || error || runResult) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="max-w-4xl mx-auto mb-10"
            >
              {/* Guard Clause: Check if strategist data exists */}
              {runResult && (!runResult.strategist || !runResult.strategist.strategy) && (
                <Card className="text-left">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                      Eroare de date
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground">
                      Datele strategiei nu sunt disponibile. Te rugăm să încerci din nou.
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Only render if data is valid */}
              {(!runResult || (runResult.strategist && runResult.strategist.strategy)) && (
              <Card className="text-left">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {error ? (
                      <>
                        <AlertTriangle className="w-5 h-5 text-destructive" />
                        Eroare
                      </>
                    ) : runResult ? (
                      <>
                        <Target className="w-5 h-5 text-primary" />
                        OptiRO.dev Agent Network — Rezultat
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5 text-primary" />
                        Rulează agenții...
                      </>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {error
                      ? error.includes("CORS")
                        ? "Dacă e CORS, folosește un proxy (ex: `VITE_CORS_PROXY=https://cors-anywhere.herokuapp.com/`)."
                        : error
                      : runResult
                      ? "Analiză completă finalizată"
                      : "Se descarcă pagina și se rulează agenții..."}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  {error && (
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground">
                      {error}
                    </div>
                  )}

                  {isRunning && (
                    <div className="rounded-md border bg-muted/30 p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="text-sm text-muted-foreground">
                          {currentMessage || "Se rulează agenții..."}
                        </div>
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      </div>
                      <div className="mt-3">
                        <Progress value={progress} className="h-2" />
                      </div>
                    </div>
                  )}

                  {runResult && (
                    <div className="space-y-6">
                      {/* Header with Dynamic Verdict */}
                      <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold text-foreground mb-2">
                          Scanare completă pentru {url}
                        </h2>
                        <div className="text-lg font-medium text-orange-600">
                          {runResult?.strategist?.strategy?.ai_verdict || 
                           (runResult?.strategist?.strategy?.geo_score < 50 
                            ? "⚠️ Site-ul tău este parțial invizibil pentru AI-uri precum ChatGPT."
                            : "✅ Site-ul tău este bine poziționat pentru AI-uri moderne.")}
                        </div>
                      </div>

                      {/* 5-Card Teaser Dashboard */}
                      <div className="grid md:grid-cols-2 gap-6 mb-8">
                        {/* Card 1: GEO Score */}
                        <Card className="border-2">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-primary" />
                              GEO Score
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-baseline gap-2">
                              <span className="text-4xl font-bold text-primary">{runResult?.strategist?.strategy?.geo_score ?? 0}</span>
                              <span className="text-muted-foreground">/ 100</span>
                            </div>
                            <div className="mt-3">
                              <Progress value={runResult?.strategist?.strategy?.geo_score ?? 0} className="h-3" />
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                              ⬇️ Sub media pieței tale
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              GEO Score arată cât de bine este înțeles site-ul tău de AI-urile moderne.
                            </p>
                          </CardContent>
                        </Card>

                        {/* Card 2: SEO Health */}
                        <Card className="border-2">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Stethoscope className="w-4 h-4 text-primary" />
                              SEO Health
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-baseline gap-2">
                              <span className="text-4xl font-bold text-orange-600">{runResult?.strategist?.strategy?.seo_health_score ?? 0}</span>
                              <span className="text-muted-foreground">/ 100</span>
                            </div>
                            <div className="mt-3">
                              <Progress value={runResult?.strategist?.strategy?.seo_health_score ?? 0} className="h-3" />
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                              {runResult?.strategist?.strategy?.seo_health_score < 50 
                                ? "⚠️ Probleme structurale detectate"
                                : runResult?.strategist?.strategy?.seo_health_score < 70
                                ? "🔧 Optimizări necesare"
                                : "✅ SEO tehnic bun"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {runResult?.strategist?.strategy?.strategic_analysis?.critical_gap || 
                               "Structura tehnică necesită optimizare pentru vizibilitate AI."}
                            </p>
                          </CardContent>
                        </Card>

                        {/* Card 3: AI Authority */}
                        <Card className="border-2">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Target className="w-4 h-4 text-primary" />
                              Autoritate AI
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className={`text-2xl font-semibold mb-2 ${
                              runResult?.strategist?.strategy?.brand_authority_rank?.includes('Lider') ? 'text-green-600' :
                              runResult?.strategist?.strategy?.brand_authority_rank?.includes('Emergent') ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {runResult?.strategist?.strategy?.brand_authority_rank ?? 'Necunoscut'}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {runResult?.strategist?.strategy?.brand_authority_rank?.includes('Lider')
                                ? '✅ Perceput ca sursă de referință'
                                : runResult?.strategist?.strategy?.brand_authority_rank?.includes('Emergent')
                                ? '⚠️ În proces de construire a autorității'
                                : '❌ Nu este perceput ca sursă de referință'}
                            </p>
                          </CardContent>
                        </Card>

                        {/* Card 4: Backlinks */}
                        <Card className="border-2">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Globe className="w-4 h-4 text-primary" />
                              Backlinks
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-lg font-medium text-foreground mb-2">
                              Site-ul tău: <span className="font-bold">
                                {runResult?.strategist?.strategy?.backlinks_site ?? 0}
                              </span> | 
                              Media Competitorilor: <span className="font-bold">
                                {runResult?.strategist?.strategy?.backlinks_competitor_avg ?? 12}
                              </span>
                            </p>
                            <p className="text-sm text-muted-foreground">
                              AI-urile acordă prioritate site-urilor citate frecvent de alte surse de încredere.
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Card 5: AI Quote (Full Width) */}
                      <Card className="border-2">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-primary" />
                            Percepție AI
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="rounded-lg border p-4 bg-accent/30">
                            <p className="text-sm text-foreground leading-relaxed italic">
                              "{runResult?.strategist?.strategy?.ai_perception_quote || 
                                runResult?.strategist?.strategy?.strategic_analysis?.ai_perception ||
                                'Site-ul oferă informații comerciale, dar nu este perceput ca o autoritate clară într-un domeniu specific.'}"
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                              Aceasta este percepția reală a motoarelor AI despre site-ul tău.
                            </p>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Email Capture Form */}
                      <Card className="border-2 bg-gradient-to-r from-primary/5 to-primary/10">
                        <CardHeader className="text-center">
                          <CardTitle className="text-xl font-bold text-foreground">
                            📩 Vrei raportul complet?
                          </CardTitle>
                          <CardDescription className="text-base text-muted-foreground">
                            Trimitem analiza completă pe email.
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <form onSubmit={handleEmailSubmit} className="space-y-4">
                            <div className="flex flex-col sm:flex-row gap-3">
                              <Input
                                type="email"
                                placeholder="Introdu adresa de email..."
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="flex-1 h-12 text-base"
                                required
                              />
                              <Button
                                type="submit"
                                size="lg"
                                className="px-8 text-base"
                                disabled={!email.trim() || isSubmittingEmail}
                              >
                                {isSubmittingEmail ? (
                                  <>
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    Se trimite...
                                  </>
                                ) : (
                                  <>
                                    📬 Trimite-mi raportul complet
                                  </>
                                )}
                              </Button>
                            </div>
                          </form>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </CardContent>
              </Card>
              )}
            </motion.div>
          )}

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground"
          >
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              <span>Rezultate în 30 secunde</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary" />
              <span>Nu necesită cont</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary" />
              <span>100% Gratuit pentru scanare</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
    </ErrorBoundary>
  );
}
